// src/composables/useIdleLogout.js
import { ref, onMounted, onBeforeUnmount } from "vue";
import {
  initIdleTimer,
  resetIdleTimer,
  stopIdleTimer
} from "@/utils/idleTimer";
import { sysConfig } from "~shared/config/system.cfg.js";

/**
 * useIdleLogout()
 * Central UI hook for tracking idle progress
 * (AppShell uses this to render the idle progress bar)
 */
export function useIdleLogout(options = {}) {
  const {
    timeoutMs = sysConfig.idleDelayMs, // default from config
    heartbeatMs = 1000,
    onLogout = () => {}
  } = options;

  const progress = ref(0);

  /**
   * This is the global "bridge" that the idleTimer can write into.
   * idleTimer.js calls:
   *     onProgress: pct => window.__idleProgress?.(pct)
   *
   * So we expose a function the timer can call.
   */
  window.__idleProgress = pct => {
    progress.value = pct;
  };

  onMounted(() => {
    /**
     * initIdleTimer only attaches listeners.
     * The *real* countdown starts in:
     *    - fetchUser()
     *    - silentRefresh()
     *    - authFetch()
     *
     * This hook only visualizes progress and ensures a handler exists.
     */
    initIdleTimer(onLogout, {
      timeoutMs,
      heartbeatMs,
      onProgress: pct => {
        progress.value = pct;
      }
    });

    // start fresh
    resetIdleTimer();
  });

  onBeforeUnmount(() => {
    // Cleanup listeners + timers
    stopIdleTimer();
    window.__idleProgress = null;
  });

  return {
    progress,
    resetIdleTimer,
    stopIdleTimer
  };
}
